import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTags,
  faCalendarAlt,
  faHeading,
  faAlignLeft,
  faFileLines,
  faUser,
  faArrowLeft,
  faPen,
} from '@fortawesome/free-solid-svg-icons';

const PreviewArticle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch article with editor relation
  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`/article/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setArticle(res.data);
      } catch (err) {
        console.error('Erreur lors de la récupération de l article:', err);
        alert('Erreur lors du chargement de l article');
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [id]);

  if (loading) return <p>Chargement de l'article...</p>;
  if (!article) return <p>Article introuvable.</p>;

  const { title, subtitle, subject, tags, paragraphs, imageUrl, postingDate, editor } = article;

  // Format date
  const formattedDate = postingDate
    ? new Date(postingDate).toLocaleString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

  // Parse paragraphs safely
  let parsedParagraphs = [];
  try {
    parsedParagraphs = Array.isArray(paragraphs) ? paragraphs : JSON.parse(paragraphs);
  } catch {
    parsedParagraphs = [];
  }

  // Parse tags safely
  let parsedTags = [];
  if (tags) {
    if (Array.isArray(tags)) {
      parsedTags = tags;
    } else if (typeof tags === 'string') {
      try {
        // Try parsing as JSON first
        parsedTags = JSON.parse(tags);
      } catch {
        // Fallback to splitting by comma
        parsedTags = tags
          .replace(/^\[|\]$/g, '')
          .split(',')
          .map((tag) => tag.replace(/^"|"$/g, '').trim())
          .filter((tag) => tag);
      }
    }
  }

  return (
    <div className="form-container">
      <div className="form-header">
        <button className="back-button" onClick={() => navigate('/admin/articles')}>
          <FontAwesomeIcon icon={faArrowLeft} /> Retour
        </button>
        <h3>Prévisualisation de l'article</h3>
        <button className="manage-button" onClick={() => navigate(`/admin/articles/edit/${id}`)}>
          <FontAwesomeIcon icon={faPen} /> Modifier
        </button>
      </div>

      <article style={{ maxWidth: '980px', margin: '30px auto', padding: '0 24px 60px', color: '#05012A' }}>
        {/* Editor Info */}
        {editor && (
          <div style={{ marginBottom: '20px', color: '#6b6b82' }}>
            <FontAwesomeIcon icon={faUser} /> Écrit par : <strong>{editor.name}</strong>
          </div>
        )}

        {/* Title */}
        <h1 className="article-title" style={{ marginBottom: '12px', fontSize: 'clamp(32px, 5vw, 54px)', lineHeight: 1.1 }}>
          {title}
        </h1>

        {/* Subtitle */}
        {subtitle && (
          <h2 style={{ marginBottom: '24px', color: '#6b6b82', fontWeight: 400, lineHeight: 1.5 }}>
            {subtitle}
          </h2>
        )}

        {/* Subject */}
        <p className="article-subject" style={{ marginBottom: '10px', fontWeight: 600 }}>
          {subject}
        </p>

        {/* Date */}
        <div className="article-date" style={{ marginBottom: '10px', color: '#aaa' }}>
          <FontAwesomeIcon icon={faCalendarAlt} /> {formattedDate}
        </div>

        {/* Tags */}
        {parsedTags.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <FontAwesomeIcon icon={faTags} />{' '}
            {parsedTags.map((tag, idx) => (
              <span
                key={idx}
                style={{
                  display: 'inline-block',
                  background: ['#B5122B', '#006D77', '#5A3D8E', '#D97706', '#217A4B'][idx % 5],
                  color: '#fff',
                  padding: '5px 10px',
                  margin: '0 5px 5px 0',
                  borderRadius: '3px',
                  fontSize: '0.9em',
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Image */}
        {imageUrl && (
          <div style={{ marginBottom: '20px' }}>
            <img
              src={`/${imageUrl}`}
              alt={title}
              className="article-image"
              style={{ width: '100%', aspectRatio: '16/9', objectFit: 'cover', borderRadius: '20px' }}
            />
          </div>
        )}

        {/* Paragraphs */}
        <div className="article-paragraphs">
          {parsedParagraphs.length > 0 ? (
            parsedParagraphs.map((paragraph, index) => (
              <section key={index} style={{ margin: '42px auto 0', maxWidth: '760px' }}>
                {paragraph.title && (
                  <h3 style={{ marginBottom: '8px', color: '#05012A', fontSize: '28px' }}>{paragraph.title}</h3>
                )}
                {paragraph.subtitle && (
                  <h4 style={{ marginBottom: '14px', color: '#6b6b82', fontWeight: 500 }}>
                    {paragraph.subtitle}
                  </h4>
                )}
                {paragraph.text && (
                  <p style={{ lineHeight: '1.85', color: '#24213f', fontSize: '17px' }}>{paragraph.text}</p>
                )}
              </section>
            ))
          ) : (
            <p>Aucun paragraphe disponible.</p>
          )}
        </div>
      </article>
    </div>
  );
};

export default PreviewArticle;
